import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { useProjectTasksDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { TaskIdentifier } from 'modules/tasks/lib/task'

import Actions, { PopoverActions } from './Actions'
import DestroyModal from './DestroyModal'
import DuplicateModal from './DuplicateModal'
import { filters as dataTableFilter } from './Filters'
import NoTasksMessage from './NoTasksMessage'
import { ActionType } from './actionTypes'
import { projectTasksProjections } from './projections'

const MODAL: Record<ActionType, React.FC<{ onCloseModal: () => void }>> = {
  Duplicate: DuplicateModal,
  Delete: DestroyModal,
}

interface ProjectTasksDatatableProps {
  currentView: 'table' | 'kanban'
  projectId: number
  segmentedControl?: React.ReactNode
  canManageTasks: boolean
  onClickCreateTask: () => void
}

const ProjectTasksDatatable = ({
  currentView,
  projectId,
  segmentedControl,
  canManageTasks,
  onClickCreateTask,
}: ProjectTasksDatatableProps) => {
  const { navigation } = useNavigation()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [actionType, setActionType] = useState<ActionType | null>()
  const source = useProjectTasksDataTable(projectTasksProjections(projectId), {
    variables: { projectIds: [projectId], search: debouncedSearch },
  })
  const onCloseModal = () => setActionType(null)
  const ActionModal = useMemo(() => (actionType ? MODAL[actionType] : null), [actionType])

  const actions = (
    <Actions
      segmentedControl={segmentedControl}
      onClickCreateTask={onClickCreateTask}
      canManageTasks={canManageTasks}
    />
  )

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
        infiniteScroll: false,
      }}
      sort={{
        path: 'id',
        direction: 'desc',
      }}
      filters={dataTableFilter(source)}
      rowTo={({ task }) =>
        `${navigation.tasks.taskIdAndIdentifier.fullPath({
          taskId: Number(task?.id),
          identifier: TaskIdentifier.Project,
        })}?view=${currentView}`
      }
    >
      <Box gap="s16">
        <DataTable.HeadBar
          exportFileName={i18n.t('project_management.table.tasks.export_file_name')}
          actions={actions}
          customPopoverOptions={
            <PopoverActions setActionType={setActionType} projectId={projectId} />
          }
          search={{
            placeholder: i18n.t('common.search_by'),
            value: search,
            onChange: onSearch,
          }}
        />
        <DataTable.Table emptyState={<NoTasksMessage />} selectable={() => true} autoWidth />
      </Box>
      {ActionModal && <ActionModal onCloseModal={onCloseModal} />}
    </DataTable.Provider>
  )
}

export default ProjectTasksDatatable
